import { useState, useEffect, useContext } from "react";
import { useParams, Link } from "react-router-dom";

import "./orgprofile.css";
import API from "../utils/API";
import { AuthContext } from "../utils/AuthContext";

import DivisionStaffList from "../components/DivisionStaffList";
import EditDivisionModal from "../components/EditDivisionModal";
import DistrictInfo from "../components/DistrictInfo";
import PageTitle from "../components/PageTitle";
import capitolImage from "../assets/capitol.png";
import { districtLabel } from "../utils/district";

export default function DivisionProfile() {
    const { id } = useParams();
    const { auth } = useContext(AuthContext);
    const [division, setDivision] = useState({});
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("Staff");

    const canEdit = auth.user?.contributor;
    const isLegislative = division.Organization?.group_type === "Legislative";

    useEffect(() => {
        API.getSingleDivision(id)
            .then(res => { setDivision(res.data); setLoading(false); })
            .catch(err => { console.log("Error fetching division:", err); setLoading(false); });
    }, [id]);

    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        const cleaned = ('' + phone).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone;
    };

    if (loading) {
        return (
            <div className="d-flex position-absolute top-50 start-50 translate-middle">
                <div className="spinner-border loading-spinner" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    const orgLogo = division.Organization?.logo || capitolImage;

    return (
        <div className="org-profile-page">
            {division.division_name && (
                <PageTitle title={`${division.division_name} | 'elloGovna`} />
            )}
            <div className="row organization-row org-profile-columns">
                <div className="org-profile-sidebar">
                    <img src={orgLogo}
                        className="org-image"
                        alt={"Logo for " + division.division_name}
                    />

                    <h1 className="org-profile-name mb-0 pb-0">{division.division_name}</h1>

                    {division.Organization && (
                        <p className="org-prof-head-text my-1">
                            <Link to={`/organization/${division.Organization.organization_id}`} className="org-parent-link">
                                {division.Organization.organization_name}
                            </Link>
                        </p>
                    )}

                    <div className="org-profile-badges-row mb-2">
                        <div className="org-profile-badges">
                            {division.division_type && (
                                <span className="org-group-type-badge">{division.division_type}</span>
                            )}
                            {division.district && (
                                <span className="org-group-type-badge ms-1">{districtLabel(division.district)}</span>
                            )}
                        </div>

                        <div className="org-profile-socials-link">
                            {division.website && (
                                <a className="me-2"
                                    href={division.website.startsWith("http") ? division.website : `https://${division.website}`}
                                    target="_blank" rel="noopener noreferrer" alt={"Website for " + division.division_name}
                                ><i className="bi bi-globe"></i></a>
                            )}
                            {canEdit && (
                                <i className="bi bi-pencil-square edit-icon-org" alt="Edit Division Button" data-bs-toggle="modal" data-bs-target="#editDivisionModal" />
                            )}
                        </div>
                    </div>

                    <hr className="mt-1"/>

                    <span className=" mt-0ms-2">
                        {division.street_address && (
                            <p className="org-prof-head-text mb-0 pb-0">
                                {division.street_address} 
                                {division.suite_number && `, Suite ${division.suite_number}`}<br/>
                                {division.city && `${division.city}`}
                                {division.state && `, ${division.state}`}
                                {division.zip_code && ` ${division.zip_code}`}
                            </p>
                        )}
                        <br/>
                        {division.division_phone && (
                            <p className="org-prof-head-text">{formatPhoneNumber(division.division_phone)}</p>
                        )}
                    </span>

                </div>

                <div className="org-profile-main">
                    {division.district && (
                        <>
                            <h6 className="org-selectors">
                                <span className={`me-5 org-feed-selector ${activeTab === "Staff" ? "selected" : ""}`} onClick={() => setActiveTab("Staff")}>Staff</span>
                                <span className={`me-5 org-feed-selector ${activeTab === "District Info" ? "selected" : ""}`} onClick={() => setActiveTab("District Info")}>District Info</span>
                            </h6>
                            <hr className="mb-3" />
                        </>
                    )}

                    {(activeTab === "Staff" || !division.district) && (
                        <DivisionStaffList
                            divisionId={parseInt(id)}
                            isLegislative={isLegislative}
                        />
                    )}

                    {activeTab === "District Info" && division.district && (
                        <DistrictInfo district={division.district} />
                    )}
                </div>
            </div>

            <div className="org-profile-footer" />

            <EditDivisionModal
                division={division}
                onSave={(updated) => setDivision(updated)}
            />
        </div>
    );
}
